import {Observable} from 'rxjs';
import i18next from 'i18next';

import {combineEpics} from 'web-app/util/redux-observable';
import LoginsApi from 'web-app/api/logins-api';
import EmployeeApi from 'web-app/api/employee-api';
import * as ContactApi from 'web-app/react/entities/contacts/api';
import * as AssigneeType from 'web-app/react/entities/common/role-assignee';
import * as OrganizationPersonsObservables from 'web-app/react/entities/organization-persons/epics';
import * as ContactObservables from 'web-app/react/entities/contacts/epics';
import * as EmployeeObservables from 'web-app/react/entities/employees/epics';
import * as AccountSelectors from 'web-app/react/session/account/selectors';
import {showDeleteDialogObservable, DialogChoice} from 'web-app/react/dialog';
import * as GlobalEventActions from 'web-app/react/global-event/actions';
import * as SentryService from 'web-app/services/sentry';

import * as Actions from './actions';

/**
 * Returns a deferred observable that is able to refresh the given assigneeId
 * @param assigneeType
 * @param assigneeId
 * @param organizationId
 * @returns {*}
 */
const deferredFetchAssigneeObservable = (assigneeType, assigneeId, organizationId) => {
    switch (assigneeType) {
        case AssigneeType.ASSIGNEE_TYPE_ORGANIZATION_PERSON:
            return Observable.defer(() => OrganizationPersonsObservables.fetchObservable(organizationId, assigneeId));
        case AssigneeType.ASSIGNEE_TYPE_EMPLOYEE:
            return Observable.defer(() => EmployeeObservables.fetchEmployeeObservable(assigneeId));
        case AssigneeType.ASSIGNEE_TYPE_RELATION:
            return Observable.defer(() => ContactObservables.fetchChildContactObservable(assigneeId));
        default:
            return Observable.of(Actions.assignRoleFailed.action('Unknown assignee type for change role operation'));
    }
};

export const assignRoleFlowObservable = (assignee, newEmail, newRoleId, overrideTargetType) => {
    const {assigneeId, famlyId, assigneeType, roleTargetId} = assignee;
    let assignObservable;

    switch (assigneeType) {
        case AssigneeType.ASSIGNEE_TYPE_ORGANIZATION_PERSON: {
            assignObservable = Observable.concat(
                Observable.fromPromise(
                    LoginsApi.assignRole(newRoleId, famlyId, roleTargetId, overrideTargetType),
                ).filter(() => false),
            );
            break;
        }

        case AssigneeType.ASSIGNEE_TYPE_EMPLOYEE: {
            assignObservable = Observable.concat(
                assignee.email !== newEmail
                    ? Observable.fromPromise(EmployeeApi.updateEmail(assigneeId, newEmail)).filter(() => false)
                    : Observable.empty(),
                Observable.fromPromiseDefer(() => LoginsApi.assignRole(newRoleId, famlyId, roleTargetId)).filter(
                    () => false,
                ),
            );
            break;
        }
        case AssigneeType.ASSIGNEE_TYPE_RELATION: {
            assignObservable = Observable.concat(
                assignee.email !== newEmail
                    ? ContactApi.updateChildContactEmail(assigneeId, newEmail).filter(() => false)
                    : Observable.empty(),
                Observable.fromPromiseDefer(() => LoginsApi.assignRole(newRoleId, famlyId, roleTargetId)).filter(
                    () => false,
                ),
            );
            break;
        }
        default:
            assignObservable = Observable.of(
                Actions.assignRoleFailed.action('Unknown assignee type for change role operation'),
            );
    }

    return assignObservable.concat(deferredFetchAssigneeObservable(assigneeType, assigneeId, assignee.organizationId));
};

const assignRoleFlow = action$ =>
    action$.ofType(Actions.assignRole.type).mergeMap(({payload}) => {
        const {assignee, newEmail, newRoleId, overrideTargetType} = payload;

        return assignRoleFlowObservable(assignee, newEmail, newRoleId, overrideTargetType)
            .concat(Observable.of(Actions.assignRoleSuccess.action({assignee})))
            .catch(e => Observable.of(Actions.assignRoleFailed.action(e)));
    });

const removeLogin = (action$, state$) =>
    action$.ofType(Actions.removeLogin.type).mergeMap(({payload}) => {
        const {assignee, overrideTargetType} = payload;
        const {organizationId, assigneeId, loginId, famlyId, roleTargetId, role, assigneeType} = assignee;

        // Sometimes role can't be found but we need more data before we can reproduce.
        if (!role) {
            SentryService.captureMessage('Role could not be found', {
                extra: {
                    overrideTargetType,
                    assignee: {
                        organizationId,
                        assigneeId,
                        loginId,
                        famlyId,
                        roleTargetId,
                        role,
                        assigneeType,
                    },
                },
            });
            return Observable.concat(
                Observable.of(GlobalEventActions.updateError.action(i18next.t('somethingWentWrong'))),
                Observable.of(Actions.removeLoginAborted.action({assignee})),
            );
        }

        const currentLoginId = AccountSelectors.getLoginId(state$.value);

        const getUnassignObservable = () =>
            Observable.fromPromise(
                LoginsApi.unassignRole(role.roleId, loginId, roleTargetId, overrideTargetType),
            ).filter(() => false);
        let confirmedUnassignObservable = null;

        // Parents should be warned that they're removing their own login
        if (loginId === currentLoginId && assigneeType === AssigneeType.ASSIGNEE_TYPE_RELATION) {
            confirmedUnassignObservable = showDeleteDialogObservable({
                title: i18next.t('changeRole.removeFromSelfRelationTitle'),
                description: i18next.t('changeRole.removeFromSelfRelationDescription'),
                okText: i18next.t('changeRole.removeFromSelfRelationConfirm'),
            }).mergeMap(selection => {
                if (selection === DialogChoice.OK) {
                    return showDeleteDialogObservable({
                        title: '',
                        description: i18next.t('changeRole.removeFromSelfRelationSecondDescription'),
                        okText: i18next.t('changeRole.removeFromSelfRelationConfirm'),
                    }).mergeMap(selection => {
                        if (selection === DialogChoice.OK) {
                            return getUnassignObservable();
                        } else {
                            return Observable.of(Actions.removeLoginAborted.action({assignee}));
                        }
                    });
                } else {
                    return Observable.of(Actions.removeLoginAborted.action({assignee}));
                }
            });
        } else {
            confirmedUnassignObservable = getUnassignObservable();
        }

        return confirmedUnassignObservable
            .concat(deferredFetchAssigneeObservable(assigneeType, assigneeId, assignee.organizationId))
            .concat(Observable.of(Actions.removeLoginSuccess.action(assignee)))
            .catch(e => Observable.of(Actions.removeLoginFailed.action(assignee, e.message)));
    });

const changeRole = action$ =>
    action$.ofType(Actions.changeRole.type).mergeMap(({payload}) => {
        const {assignee, newRoleId, overrideTargetType} = payload;
        const {assigneeId, assigneeType, role, loginId, famlyId, roleTargetId} = assignee;

        const assignUnassignObservable = Observable.concat(
            Observable.fromPromiseDefer(() =>
                LoginsApi.unassignRole(role.roleId, loginId, roleTargetId, overrideTargetType),
            ).filter(() => false),
            Observable.fromPromiseDefer(() =>
                LoginsApi.assignRole(newRoleId, famlyId, roleTargetId, overrideTargetType),
            ).filter(() => false),
        );

        return assignUnassignObservable
            .concat(deferredFetchAssigneeObservable(assigneeType, assigneeId, assignee.organizationId))
            .concat(Observable.of(Actions.changeRoleSuccess.action({assignee})))
            .catch(e => Observable.of(Actions.changeRoleFailed.action(e)));
    });

export default combineEpics(assignRoleFlow, removeLogin, changeRole);
